import { useMutation } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { DeleteGroupResponseSchema } from "../response/DeleteGroupResponse";

/**
 * Mendefinisikan tipe data untuk variabel yang dibutuhkan oleh fungsi mutasi.
 * Dalam kasus ini, hanya ID grup yang diperlukan.
 */
interface DeleteGroupVariables {
    id: string;
}

/**
 * Custom hook untuk menghapus grup.
 * Menggunakan @tanstack/react-query's useMutation untuk menangani operasi DELETE.
 *
 * @returns Hasil dari useMutation, termasuk fungsi `mutate` dan status seperti `isPending`.
 */
export const useDeleteGroup = () => {
    return useMutation({
        /**
         * mutationFn adalah fungsi yang akan dieksekusi saat `mutate` dipanggil.
         * @param variables - Objek yang berisi ID grup yang akan dihapus.
         */
        mutationFn: async ({ id }: DeleteGroupVariables) => {
            // Memastikan ID ada sebelum mencoba menghapus.
            if (!id) {
                throw new Error("Group ID is required for deletion.");
            }

            try {
                // Kirim request DELETE ke endpoint spesifik grup.
                const response = await privateApi.delete(`/v4/group/${id}`);
                return response.data;
            } catch (error) {
                console.error(`Failed to delete group with ID ${id}:`, error);
                // Melempar kembali error agar bisa ditangani oleh callback `onError`
                // di komponen yang menggunakan hook ini.
                throw error;
            }
        },

        /**
         * onSuccess dipanggil setelah mutasi berhasil untuk validasi data.
         * @param responseData - Data yang dikembalikan oleh API.
         */
        onSuccess: (responseData) => {
            // Validasi data yang diterima dari API untuk memastikan formatnya benar.
            // Untuk DELETE, kita mengharapkan respons tanpa data entitas (data: null).
            const validationResult = DeleteGroupResponseSchema.safeParse(responseData);
            if (!validationResult.success) {
                console.error("API Response Validation Failed on Success:", validationResult.error.errors);
                // Melempar error jika respons tidak sesuai dengan yang diharapkan.
                throw new Error("Invalid data format received from the server after deletion.");
            }
        },
    });
};